import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { 
  Plus, Calendar, MapPin, Users, Copy, Check, 
  Loader2, Trash2, Edit2, PartyPopper, QrCode, Image, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import EventLogoUpload from '../../components/events/EventLogoUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizerEvents = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [selectedEventForLogo, setSelectedEventForLogo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    school_name: '',
    event_name: '',
    event_date: '',
    venue: '',
    tagline: '',
    description: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/events`, formData);
      const newEvent = response.data;
      const isFirstEvent = events.length === 0;
      
      setEvents([newEvent, ...events]);
      setCreateDialogOpen(false);
      setFormData({
        school_name: '',
        event_name: '',
        event_date: '',
        venue: '',
        tagline: '',
        description: ''
      });
      
      // If this is the first event, redirect to subscription page
      if (isFirstEvent) {
        toast.success('Event created! Now choose a subscription plan.');
        navigate('/organizer/subscription', { 
          state: { 
            fromEventCreation: true, 
            eventCode: newEvent.code 
          }
        });
      } else {
        toast.success(`Event created! Share code: ${newEvent.code}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? All associated data (attendees, announcements, payments) will be permanently deleted.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/events/${eventId}`);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Event deleted successfully');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const copyEventCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Event code copied to clipboard!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading) {
    return (
      <OrganizerLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </OrganizerLayout>
    );
  }

  return (
    <OrganizerLayout>
      <div className="space-y-8" data-testid="organizer-events">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon">My Events</h1>
            <p className="text-gray-600 mt-1">Create and manage your reunion events</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full" data-testid="create-event-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-maroon">Create New Event</DialogTitle>
                <DialogDescription>
                  Set up a new reunion event. You'll get a unique code to share with attendees.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>School Name *</Label>
                  <Input
                    value={formData.school_name}
                    onChange={(e) => setFormData({...formData, school_name: e.target.value})}
                    placeholder="e.g., Central University"
                    required
                    className="bg-cream-paper"
                    data-testid="event-school-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Event Name *</Label>
                  <Input
                    value={formData.event_name}
                    onChange={(e) => setFormData({...formData, event_name: e.target.value})}
                    placeholder="e.g., Batch 2000 Silver Reunion"
                    required
                    className="bg-cream-paper"
                    data-testid="event-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Event Date</Label>
                    <Input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                      className="bg-cream-paper"
                      data-testid="event-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Venue</Label>
                    <Input
                      value={formData.venue}
                      onChange={(e) => setFormData({...formData, venue: e.target.value})}
                      placeholder="e.g., Shangri-La"
                      className="bg-cream-paper"
                      data-testid="event-venue"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input
                    value={formData.tagline}
                    onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                    placeholder="e.g., 25 Years of Memories"
                    className="bg-cream-paper"
                    data-testid="event-tagline"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description of the reunion event..."
                    className="bg-cream-paper"
                    rows={3}
                    data-testid="event-description"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-maroon hover:bg-maroon-hover text-white"
                    disabled={creating}
                    data-testid="submit-event-btn"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Event'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Events List */}
        {events.length === 0 ? (
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="py-16 text-center">
              <PartyPopper className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                No Events Yet
              </h3>
              <p className="text-gray-500 mb-6">
                Create your first reunion event to get started!
              </p>
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="bg-maroon hover:bg-maroon-hover text-white rounded-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Event
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {events.map((event) => (
              <Card key={event.id} className="border-0 shadow-sm bg-white overflow-hidden" data-testid={`event-card-${event.code}`}>
                <div className="flex flex-col lg:flex-row">
                  {/* Event Code Section */}
                  <div className="bg-gradient-to-br from-maroon to-maroon-hover p-6 lg:w-64 flex flex-col items-center justify-center text-white">
                    <p className="text-xs uppercase tracking-wider text-white/70 mb-2">Event Code</p>
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-4 mb-3">
                      <p className="font-mono text-3xl font-bold tracking-widest" data-testid={`event-code-${event.code}`}>
                        {event.code}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                      onClick={() => copyEventCode(event.code)}
                      data-testid={`copy-code-${event.code}`}
                    >
                      {copiedCode === event.code ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Event Details */}
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-xl font-bold text-gray-900">
                          {event.school_name}
                        </h3>
                        <p className="text-lg text-maroon font-medium">{event.event_name}</p>
                        {event.tagline && (
                          <p className="text-sm text-gray-500 italic mt-1">"{event.tagline}"</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteEvent(event.id)}
                        data-testid={`delete-event-${event.code}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {event.event_date && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="h-4 w-4 text-maroon" />
                          <span className="text-sm">
                            {new Date(event.event_date).toLocaleDateString('en-PH', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      )}
                      {event.venue && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="h-4 w-4 text-maroon" />
                          <span className="text-sm">{event.venue}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="h-4 w-4 text-maroon" />
                        <span className="text-sm">{event.attendee_count || 0} attendees</span>
                      </div>
                    </div>

                    {event.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">{event.description}</p>
                    )}

                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <Badge className="bg-gold/20 text-gold-dark">
                        Created {new Date(event.created_at).toLocaleDateString()}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-auto"
                        onClick={() => setSelectedEventForLogo(event)}
                        data-testid={`event-logo-btn-${event.code}`}
                      >
                        <Image className="h-4 w-4 mr-1" />
                        {event.logo ? 'Change Logo' : 'Add Logo'}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Logo Upload Dialog */}
        <Dialog open={!!selectedEventForLogo} onOpenChange={(open) => !open && setSelectedEventForLogo(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Event Logo</DialogTitle>
              <DialogDescription>
                Upload a custom logo for {selectedEventForLogo?.school_name}
              </DialogDescription>
            </DialogHeader>
            {selectedEventForLogo && (
              <EventLogoUpload 
                event={selectedEventForLogo} 
                onLogoUpdate={(updatedEvent) => {
                  setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
                  setSelectedEventForLogo(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Instructions Card */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-cream to-cream-paper">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-maroon flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              How to Invite Attendees
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-gray-600">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-maroon text-white text-sm flex items-center justify-center flex-shrink-0">1</span>
                <span>Share the <strong>Event Code</strong> with your batchmates via messaging apps, email, or social media</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-maroon text-white text-sm flex items-center justify-center flex-shrink-0">2</span>
                <span>Attendees go to the <strong>Attendee Login</strong> page and enter the event code</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-maroon text-white text-sm flex items-center justify-center flex-shrink-0">3</span>
                <span>After registration, they'll have access to announcements, payments, and event details</span>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </OrganizerLayout>
  );
};

export default OrganizerEvents;
